import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";

import moment from "moment";
import placeholder from "../../assets/svgs/placeholder.svg";
import {
  HelpIconContainer,
  HelperTextPopup,
  HelperTextContent,
} from "../../styles/AddNewBundleDeal.styled";
import {
  OrderDetailContainer,
  HeaderSection,
  HeaderTitle,
  CardContent,
  MobileHeaderInfo,
  MobileHeaderTop,
  MobileHeaderDetails,
  MobileSingleColumn,
  MobileItemsSection,
  MobileItemsTitle,
  MobileItemsList,
  MobileOrderItem,
  MobileItemImage,
  MobileItemContent,
  MobileItemName,
  MobileItemPricing,
  MobileQuantity,
  MobilePrice,
  MobileSummarySection,
  MobileSummaryList,
  MobileSummaryRow,
  MobileSummaryLabel,
  MobileSummaryValue,
  MobileTotalRow,
  MobileTotalLabel,
  MobileTotalValue,
  MobileFoodOptionsContainer,
  MobileFoodOptionType,
  MobileFoodOptionTypeName,
  MobileFoodOptionsList,
  MobileFoodOptionItem,
  MobileFoodOptionName,
} from "../../styles/OrderDetail.styled";

import {
  LoadingOverlay,
  LoadingSpinner,
  LoadingSpinnerIcon,
} from "../../styles/loading";

import { alertMessage } from "../../services/alertService";
import { getObj } from "../../services/HTTPServices";
import { ROUTES } from "../../constants";
import { SendButton } from "../../styles/AddNewFoodMenu.styled";
import { LanguageText } from "../../styles";
import { PageTitle } from "../../styles/MenuManagement.styled";

// Custom Help Icon component to replace MUI HelpIcon
const HelpIcon = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: #cccccc;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  margin-left: 4px;

  &:before {
    content: "?";
  }
`;

interface OrderDetailState {
  order: any;
  loading: boolean;
  error: string | null;
  helperTextVisible: {
    [key: string]: boolean;
  };
}

const OrderDetailMobile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<OrderDetailState>({
    order: null,
    loading: true,
    error: null,
    helperTextVisible: {},
  });

  const toggleHelperText = (key: string) => {
    setState((prev) => ({
      ...prev,
      helperTextVisible: {
        ...prev.helperTextVisible,
        [key]: !prev.helperTextVisible[key],
      },
    }));
  };

  useEffect(() => {
    if (id) {
      fetchOrderDetails(id);
    }
  }, [id]);

  const fetchOrderDetails = async (orderId: string) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      let res: any = await getObj("order/GetOrderById?id=", "order", orderId);
      if (res && res.data) {
        setTimeout(() => {
          setState((prev) => ({
            ...prev,
            order: res.data,
            loading: false,
          }));
        }, 500);
      }
    } catch (error) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: "Failed to load order details",
      }));
      alertMessage("Error!", "Failed to load order details");
    }
  };

  const calculateSubtotal = () => {
    if (!state.order?.orderFoodMenus) return 0;
    return state.order.orderFoodMenus.reduce(
      (sum: number, item: any) => sum + item.lineTotal,
      0
    );
  };

  if (state.loading) {
    return (
      <LoadingOverlay>
        <LoadingSpinner>
          <LoadingSpinnerIcon />
        </LoadingSpinner>
      </LoadingOverlay>
    );
  }

  if (state.error || !state.order) {
    return (
      <OrderDetailContainer>
        <HeaderSection>
          <HeaderTitle>Order Details</HeaderTitle>
        </HeaderSection>
        <CardContent>
          <p>Order not found or failed to load.</p>
        </CardContent>
      </OrderDetailContainer>
    );
  }

  const { order } = state;
  const subtotal = calculateSubtotal();

  return (
    <OrderDetailContainer>
      {/* <FlexContainer className="pl-16">
        <img
          src={Back}
          alt="back"
          onClick={() => navigate(ROUTES.ORDERS)}
          className="pointer"
        />
        <span className="ml-48 f-16 fw-700">Order Details</span>
      </FlexContainer>
      <br></br> */}
      <PageTitle>Order Details</PageTitle>
      <HeaderSection>
        <MobileHeaderInfo>
          <MobileHeaderTop className="mb-8">
            <HeaderTitle>Order No #{order.orderNo}</HeaderTitle>
          </MobileHeaderTop>
          <MobileHeaderDetails>
            <div className="mb-8">
              Time: {moment(order.orderDate).format("DD/MM/YYYY h:mm A")}
            </div>
            <div>Delivery Buddy: {order.courierName}</div>
          </MobileHeaderDetails>
        </MobileHeaderInfo>
      </HeaderSection>

      <br></br>

      {/* Single Column Layout */}
      <MobileSingleColumn>
        {/* Items Section */}
        <MobileItemsSection>
          <MobileItemsTitle>Items:</MobileItemsTitle>
          <MobileItemsList>
            {order.orderFoodMenus?.map((item: any, index: number) => (
              <MobileOrderItem key={item.id || index}>
                <MobileItemImage
                  src={item.photoPath || placeholder}
                  alt={item.item}
                />
                <MobileItemContent>
                  <MobileItemName>{item.item}</MobileItemName>
                  {item.foodOptionsTypes &&
                    item.foodOptionsTypes.length > 0 && (
                      <MobileFoodOptionsContainer>
                        {item.foodOptionsTypes.map(
                          (optionType: any, index: number) => (
                            <MobileFoodOptionType
                              key={optionType.foodOptionsTypeId || index}
                            >
                              <MobileFoodOptionTypeName>
                                {optionType.name}
                              </MobileFoodOptionTypeName>
                              <MobileFoodOptionsList>
                                {optionType.foodOptions?.map(
                                  (option: any, optionIndex: number) => (
                                    <MobileFoodOptionItem
                                      key={option.foodOptionsId || optionIndex}
                                    >
                                      <MobileFoodOptionName>
                                        {option.name} &nbsp; - &nbsp;{" "}
                                        {option.count} {option.unit || "pc"}
                                      </MobileFoodOptionName>
                                    </MobileFoodOptionItem>
                                  )
                                )}
                              </MobileFoodOptionsList>
                            </MobileFoodOptionType>
                          )
                        )}
                      </MobileFoodOptionsContainer>
                    )}
                </MobileItemContent>
                <MobileItemPricing>
                  <MobileQuantity>x{item.qty}</MobileQuantity>
                  <MobilePrice>$ {item.price.toFixed(2)}</MobilePrice>
                </MobileItemPricing>
              </MobileOrderItem>
            ))}
          </MobileItemsList>
        </MobileItemsSection>

        {/* Summary Section */}
        <MobileSummarySection>
          <MobileSummaryList>
            <MobileSummaryRow>
              <MobileSummaryLabel>Subtotal</MobileSummaryLabel>
              <MobileSummaryValue>$ {subtotal.toFixed(2)}</MobileSummaryValue>
            </MobileSummaryRow>

            <MobileSummaryRow>
              <MobileSummaryLabel>
                Merchant Delivery Fee
                <HelpIconContainer
                  className="help-icon-container"
                  onMouseOver={() => toggleHelperText("bundlePhoto")}
                  onMouseOut={() => toggleHelperText("bundlePhoto")}
                >
                  <HelpIcon />
                  {state.helperTextVisible.bundlePhoto && (
                    <HelperTextPopup
                      $visible={state.helperTextVisible.bundlePhoto}
                    >
                      <HelperTextContent>
                        Merchant Delivery Fee
                      </HelperTextContent>
                    </HelperTextPopup>
                  )}
                </HelpIconContainer>
              </MobileSummaryLabel>
              <MobileSummaryValue>
                -$ {order.spDeliveryFees?.toFixed(2) || ""}
              </MobileSummaryValue>
            </MobileSummaryRow>

            <MobileSummaryRow>
              <MobileSummaryLabel>Merchant Discount</MobileSummaryLabel>
              <MobileSummaryValue>
                -$ {order.discount?.toFixed(2) || ""}
              </MobileSummaryValue>
            </MobileSummaryRow>

            <MobileTotalRow>
              <MobileTotalLabel>You'll receive</MobileTotalLabel>
              <MobileTotalValue>
                ${" "}
                {(subtotal - order.discount - order.spDeliveryFees)?.toFixed(
                  2
                ) || ""}
              </MobileTotalValue>
            </MobileTotalRow>
          </MobileSummaryList>
        </MobileSummarySection>
      </MobileSingleColumn>


      <br></br>

      <SendButton onClick={() => navigate(ROUTES.ORDERS)}>
        <LanguageText>Back</LanguageText>
      </SendButton>
    </OrderDetailContainer>
  );
};

export default OrderDetailMobile;
